import React, { useMemo, useState } from 'react';
import { MarketButton, MarketHeader } from '@market/react';
import { CountryCode } from 'libphonenumber-js';
import { useRequestAddProfileIdentifierMutation } from 'store/query';
import useValidation from 'utils/custom-react-hooks/useValidation';
import {
  ContactInformationIdentifierType,
  IdentifierType,
} from 'routes/profile/models/Identifier';
import { isValidIdentifier } from 'utils/identifiers';
import IdentifierValueInput from 'components/IdentifierValueInput';
import { openToast, ToastVariant } from 'store/toastSlice';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Country } from 'rpc/model/squareup/common/countries';
import { getCountryFromLocale } from 'utils/globalAddress';
import { languageDetector } from 'i18n';
import { isPreconditionFailedError } from 'store/query/utils';

type RequestProps = {
  onRequestAdd: (
    identifierType: ContactInformationIdentifierType,
    identifierValue: string
  ) => void;
};

const Request: React.FC<RequestProps> = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [identifierType, setIdentifierType] =
    useState<ContactInformationIdentifierType>(IdentifierType.Email);
  const [identifierValue, setIdentifierValue] = useState<string>('');

  const phoneCountryCode = useMemo<CountryCode>(() => {
    const locale = languageDetector.detect();
    const localeCountry = getCountryFromLocale(locale);
    return localeCountry === undefined
      ? 'US'
      : (Country[localeCountry] as CountryCode);
  }, []);

  const [isInvalid, resetValidation, validate] = useValidation(
    (type: ContactInformationIdentifierType, value: string) =>
      isValidIdentifier(type, value, phoneCountryCode)
  );

  const [requestAddProfileIdentifier, requestAddProfileIdentifierResult] =
    useRequestAddProfileIdentifierMutation();

  const requestAddIdentifier = async () => {
    if (!validate(identifierType, identifierValue)) {
      return;
    }

    await requestAddProfileIdentifier({
      identifierType,
      identifierValue,
    })
      .unwrap()
      .then(() => {
        props.onRequestAdd(identifierType, identifierValue);
      })
      .catch((ex) => {
        // When the identifier already exists on the buyer's profile (but isn't a
        // verified, selectable identifier), the backend returns PRECONDITION_FAILED.
        // Surface an actionable message instead of the generic error so the buyer
        // knows to verify it from their profile rather than re-adding it.
        let message = t('common.somethingWentWrong.retryable.direct');
        if (isPreconditionFailedError(ex)) {
          message = t(
            identifierType === IdentifierType.Phone
              ? 'profile.account.contactInfo.addContactInfoModal.request.phoneAlreadyExists'
              : 'profile.account.contactInfo.addContactInfoModal.request.emailAlreadyExists'
          );
        }

        dispatch(
          openToast({
            message,
            variant: ToastVariant.Critical,
          })
        );
      });
  };

  const isSaveButtonDisabled = requestAddProfileIdentifierResult.isLoading;

  return (
    <>
      <MarketHeader>
        <MarketButton
          rank={'primary'}
          slot={'actions'}
          {...(isSaveButtonDisabled && { disabled: true })}
          onClick={requestAddIdentifier}
        >
          {t('common.next')}
        </MarketButton>
      </MarketHeader>
      <h2 className={'heading-30 mt-6 mb-8'}>
        {t(
          'profile.account.contactInfo.addContactInfoModal.request.addContactInfo'
        )}
      </h2>
      <div>
        <IdentifierValueInput
          emailInputDataTestId={'email-input'}
          phoneInputDataTestId={'phone-input'}
          identifierType={identifierType}
          identifierValue={identifierValue}
          countryCode={phoneCountryCode}
          onEnterPressed={requestAddIdentifier}
          isInvalid={isInvalid}
          onIdentifierTypeChanged={(newIdentifierType) => {
            setIdentifierType(newIdentifierType);
            resetValidation();
          }}
          onIdentifierValueChanged={(newIdentifierValue) => {
            setIdentifierValue(newIdentifierValue);
            resetValidation();
          }}
          enableTypeSelection
        />
      </div>
    </>
  );
};

export default Request;
